import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, ArrowLeft, ArrowRight, RotateCcw } from "lucide-react";

const STOPS = [
  { id: "hero", title: "Welcome in", text: "This is Novcovek. Seven health platforms behind one address, and you stay anonymous in all of them. They orbit you, not the other way around." },
  { id: "platforms", title: "The seven colours", text: "Open a colour and it unfolds. Each platform is a complete world with its own tools, people and rooms." },
  { id: "day", title: "One ordinary day", text: "This is how it feels in practice: one thread running through the hours you already live." },
  { id: "breath", title: "A small pause", text: "Even the homepage slows down here. One breath, on your rhythm, whenever you want it." },
  { id: "alias", title: "Your other name", text: "A made up name and a colour is all anyone sees. Even the specialists answering you." },
  { id: "voices", title: "The people answering", text: "Doctors, therapists and coaches under their real names. Their words drift past here." },
  { id: "journal", title: "And the reading room", text: "Short plain explainers from the specialists. That is the whole tour." },
];

export default function Tour() {
  const [step, setStep] = useState(-1);
  const [prompt, setPrompt] = useState(false);

  useEffect(() => {
    const start = () => { setPrompt(false); setStep(0); };
    window.addEventListener("nc-start-tour", start);
    if (!localStorage.getItem("nc_tour_seen")) {
      const t = setTimeout(() => setPrompt(true), 1600);
      return () => { clearTimeout(t); window.removeEventListener("nc-start-tour", start); };
    }
    return () => window.removeEventListener("nc-start-tour", start);
  }, []);

  useEffect(() => {
    if (step >= 0 && STOPS[step]) {
      document.getElementById(STOPS[step].id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [step]);

  const close = (backToTop) => {
    localStorage.setItem("nc_tour_seen", "1");
    setStep(-1);
    setPrompt(false);
    if (backToTop) setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 150);
  };

  return (
    <>
      <AnimatePresence>
        {prompt && step < 0 && (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            className="fixed bottom-24 right-5 z-[60] glass-strong rounded-3xl p-5 w-72"
            data-testid="tour-prompt"
          >
            <button onClick={() => close(false)} className="absolute top-3 right-3 text-[var(--ink-soft)] hover:text-[var(--ink)] transition-colors" data-testid="tour-prompt-dismiss" aria-label="Dismiss">
              <X size={15} />
            </button>
            <span className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ background: "#EFEBFD", color: "#5D48C0" }}>
              <Sparkles size={17} />
            </span>
            <p className="font-display font-semibold mt-3">First time here?</p>
            <p className="text-xs text-[var(--ink-soft)] mt-1">A short walk around the page. Thirty seconds, then back where you started.</p>
            <button onClick={() => { setPrompt(false); setStep(0); }} className="btn-ink w-full rounded-full py-2.5 mt-4 text-xs font-semibold" data-testid="tour-prompt-start">
              Show me around
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {step >= 0 && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 30 }}
            className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[60] glass-strong rounded-3xl p-5 w-[calc(100vw-2.5rem)] max-w-md"
            data-testid="tour-card"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="kicker" style={{ color: "#5D48C0" }}>Stop {step + 1} of {STOPS.length}</p>
                <p className="font-display text-lg font-semibold mt-1" data-testid="tour-step-title">{STOPS[step].title}</p>
                <p className="text-sm text-[var(--ink-soft)] mt-1">{STOPS[step].text}</p>
              </div>
              <button onClick={() => close(false)} className="text-[var(--ink-soft)] hover:text-[var(--ink)] transition-colors shrink-0" data-testid="tour-skip-btn" aria-label="Skip tour">
                <X size={16} />
              </button>
            </div>
            <div className="flex items-center justify-between mt-4">
              <div className="flex gap-1.5">
                {STOPS.map((_, i) => (
                  <span key={i} className="h-1.5 rounded-full transition-all duration-300" style={{ width: i === step ? "20px" : "6px", background: i <= step ? "#8B72EC" : "rgba(46,51,56,0.15)" }} />
                ))}
              </div>
              <div className="flex gap-2">
                {step > 0 && (
                  <button onClick={() => setStep(step - 1)} className="btn-line rounded-full px-3.5 py-2 text-xs font-semibold inline-flex items-center gap-1.5" data-testid="tour-back-btn">
                    <ArrowLeft size={13} /> Back
                  </button>
                )}
                {step < STOPS.length - 1 ? (
                  <button onClick={() => setStep(step + 1)} className="btn-ink rounded-full px-4 py-2 text-xs font-semibold inline-flex items-center gap-1.5" data-testid="tour-next-btn">
                    Next <ArrowRight size={13} />
                  </button>
                ) : (
                  <button onClick={() => close(true)} className="btn-ink rounded-full px-4 py-2 text-xs font-semibold inline-flex items-center gap-1.5" data-testid="tour-finish-btn">
                    <RotateCcw size={13} /> Back to the start
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
